import { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import useAuthPersistStore from "../../store/authPersistStore";
import { useSubscribeToNewsletter } from "../../hooks/useSubscribeToNewsletter";
import { ReactComponent as CloseIcon } from "../../assets/icons/cancel.svg";
import logo from "../../assets/images/logo.svg";
import backgroundImage from "../../assets/images/background.jpg";

const STORAGE_KEY = "neoum-newsletter-popup";
const DELAY_MS = 45_000;

const validationSchema = Yup.object({
  fullName: Yup.string().trim().required("Ime je obavezno"),
  email: Yup.string()
    .trim()
    .email("Unesi ispravan email")
    .required("Email je obavezan"),
  consent: Yup.boolean().oneOf(
    [true],
    "Potrebna je saglasnost za prijavu na NeoPoštu"
  ),
});

const NewsletterPopup = () => {
  const { isAuthenticated } = useAuthPersistStore();
  const { mutateAsync, isLoading } = useSubscribeToNewsletter();
  const [isOpen, setIsOpen] = useState(false);

  const wasShown = () => {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "dismissed" || value === "subscribed";
  };

  useEffect(() => {
    if (isAuthenticated || wasShown()) {
      setIsOpen(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setIsOpen(true);
    }, DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [isAuthenticated]);

  const closeAndRemember = (status: "dismissed" | "subscribed") => {
    localStorage.setItem(STORAGE_KEY, status);
    setIsOpen(false);
  };

  if (isAuthenticated || !isOpen) {
    return null;
  }

  return (
    <div className="newsletter-popup" role="dialog" aria-modal="true">
      <div
        className="newsletter-popup_backdrop"
        onClick={() => closeAndRemember("dismissed")}
      />
      <div className="newsletter-popup_modal">
        <button
          type="button"
          className="newsletter-popup_close"
          aria-label="Zatvori"
          onClick={() => closeAndRemember("dismissed")}
        >
          <CloseIcon />
        </button>

        <div className="newsletter-popup_hero">
          <img
            src={backgroundImage}
            alt=""
            className="newsletter-popup_hero-image"
          />
          <div className="newsletter-popup_hero-content">
            <img src={logo} alt="NeoUm Media" className="newsletter-popup_logo" />
            <p className="newsletter-popup_tagline">
              Najveća platforma praktičnog znanja na Balkanu!
            </p>
          </div>
        </div>

        <div className="newsletter-popup_body">
          <h2 className="newsletter-popup_title">NeoPošta</h2>
          <p className="newsletter-popup_intro">
            Prijavom na NeoPoštu dobijaš mogućnost otkrivanja najnovijih
            informacija i edukacija iz oblasti neurobiologije, psihologije i
            duhovnosti, koje mogu biti ključ ka produbljivanju tvog znanja i
            razumevanja sveta uma i duše!
          </p>

          <Formik
            initialValues={{
              fullName: "",
              email: "",
              consent: false,
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                await mutateAsync({
                  fullName: values.fullName.trim(),
                  email: values.email.trim(),
                  consent: values.consent,
                });
                toast.success("Uspešno si prijavljen/a na NeoPoštu.");
                closeAndRemember("subscribed");
              } catch {
                toast.error(
                  "Prijava nije uspela. Pokušaj ponovo malo kasnije."
                );
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
            }) => (
              <Form className="newsletter-popup_form">
                <label className="newsletter-popup_field">
                  <span className="newsletter-popup_label">Ime</span>
                  <input
                    name="fullName"
                    type="text"
                    placeholder="Unesi ime"
                    value={values.fullName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="name"
                  />
                  {touched.fullName && errors.fullName && (
                    <span className="newsletter-popup_error">
                      {errors.fullName}
                    </span>
                  )}
                </label>

                <label className="newsletter-popup_field">
                  <span className="newsletter-popup_label">Email</span>
                  <input
                    name="email"
                    type="email"
                    placeholder="Unesi email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="email"
                  />
                  {touched.email && errors.email && (
                    <span className="newsletter-popup_error">
                      {errors.email}
                    </span>
                  )}
                </label>

                <p className="newsletter-popup_note">
                  Sa ovih mejlova se u svakom momentu možeš odjaviti klikom na
                  dugme &quot;Odjava&quot;.
                </p>

                <div className="newsletter-popup_permissions">
                  <h3>Dozvole za marketing</h3>
                  <p>
                    Informacije koje navedeš u ovom obrascu će se koristiti samo
                    da bismo ti pružili informacije iz oblasti ličnog rasta i
                    razvoja. Tvoja privatnost nam je važna! Označi polje da
                    daješ saglasnost u vezi sa tim:
                  </p>

                  <label className="newsletter-popup_consent">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={values.consent}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <span>DAJEM SAGLASNOST DA PRIMAM NEOPOŠTU</span>
                  </label>
                  {touched.consent && errors.consent && (
                    <span className="newsletter-popup_error">
                      {errors.consent}
                    </span>
                  )}

                  <p className="newsletter-popup_disclaimer">
                    Povremeno ćemo ti slati mejlove o promocijama, novim
                    sadržajima i važnim obaveštenjima kako bi bio/la u toku.
                  </p>
                </div>

                <button
                  type="submit"
                  className="newsletter-popup_submit"
                  disabled={isSubmitting || isLoading}
                >
                  {isSubmitting || isLoading ? "Slanje..." : "Prijavi se"}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
